import { ArrowRight, Eye, EyeOff, House, Lock, Monitor, Phone, Smartphone, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginApi } from '../../api/auth.api';
import styles from '../../assets/scss/pages/AdminLogin.module.scss';
import { useAuthStore } from '../../stores/authStore';
import loginBg from '../../assets/img/poter_login.png';
import logo from '../../assets/img/logo.png';
import { version } from '../../config/version-code';

const ALLOWED_ROLES = ['admin', 'manage', 'employee', 'checkin'];

// Tài khoản checkin chỉ vào màn check-in (giống app)
const getHomePath = (role) => (role === 'checkin' ? '/checkin' : '/admin/dashboard');


export default function AdminLoginPage() {
    const navigate = useNavigate();
    const { isAuthenticated, user, login } = useAuthStore();

    const [formData, setFormData] = useState({ userName: '', passWord: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [bgLoaded, setBgLoaded] = useState(false);

    // Redirect nếu đã đăng nhập
    useEffect(() => {
        if (isAuthenticated && user && ALLOWED_ROLES.includes(user.role)) {
            navigate(getHomePath(user.role), { replace: true });
        }
    }, [isAuthenticated, user, navigate]);

    // Load saved credentials
    useEffect(() => {
        const saved = localStorage.getItem('rememberedLogin');
        if (saved) {
            try {
                const { userName } = JSON.parse(saved);
                setFormData((prev) => ({ ...prev, userName }));
                setRememberMe(true);
            } catch {
                // ignore
            }
        }
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'userName') {
            const digits = value.replace(/\D/g, '').slice(0, 10);
            setFormData((prev) => ({ ...prev, userName: digits }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const phone = formData.userName.trim();
        const password = formData.passWord.trim();

        if (!phone || !password) {
            setError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        if (!/^0\d{9}$/.test(phone)) {
            setError('Số điện thoại không hợp lệ (10 số, bắt đầu bằng 0)');
            return;
        }

        setIsLoading(true);

        try {
            const { data } = await loginApi({
                userName: phone,
                passWord: password,
            });

            if (!ALLOWED_ROLES.includes(data.user.role)) {
                setError('Tài khoản không có quyền truy cập trang quản lý');
                return;
            }

            // Lưu remember me
            if (rememberMe) {
                localStorage.setItem('rememberedLogin', JSON.stringify({ userName: phone }));
            } else {
                localStorage.removeItem('rememberedLogin');
            }

            // Lưu vào auth store
            login(data);

            navigate(getHomePath(data.user.role), { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message;
            // Ưu tiên message từ backend (vd: tài khoản bị khoá / hết hạn) thay vì
            // ghi đè cứng theo status — backend trả cùng 401 cho mọi trường hợp.
            setError(msg || 'Số điện thoại hoặc mật khẩu không đúng');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.adminLogin}>
            {/* ===== LEFT PANEL ===== */}
            <div className={styles.adminLoginLeft}>
                {!bgLoaded && <div className={styles.adminLoginBgSkeleton} />}
                <img src={loginBg} alt="Navo Sổ Tiệm" className={styles.adminLoginBgImage} onLoad={() => setBgLoaded(true)} />
                <div className={styles.adminLoginBgOverlay} />

                <div className={styles.adminLoginLeftContent}>
                    <div className={styles.adminLoginHero} />
                    <div className={styles.adminLoginFooter}>
                        &copy; 2025 Navo Sổ Tiệm. Phiên bản: {version}
                    </div>
                </div>
            </div>

            {/* ===== RIGHT PANEL ===== */}
            <div className={styles.adminLoginRight}>
                <div className={styles.adminLoginBrand}>
                    <img src={logo} alt="Navo logo" className={styles.adminLoginBrandLogo} />
                    <span className={styles.adminLoginBrandName}>Navo Sổ Tiệm</span>
                </div>
                <button className={styles.adminLoginHomeBtn} onClick={() => navigate('/')}>
                    <House size={16} /> Trang chủ
                </button>
                <div className={styles.adminLoginFormWrapper}>
                    <h2 className={styles.adminLoginTitle}>Đăng Nhập</h2>
                    <p className={styles.adminLoginSubtitle}>Chào mừng bạn quay lại với hệ thống quản lý Navo</p>

                    <form onSubmit={handleSubmit} className={styles.adminLoginForm}>
                        {/* Số điện thoại */}
                        <div className={styles.adminLoginField}>
                            <label className={styles.adminLoginLabel}>SỐ ĐIỆN THOẠI</label>
                            <div className={styles.adminLoginInputWrapper}>
                                <span className={styles.adminLoginInputIcon}>
                                    <Phone />
                                </span>
                                <input
                                    type="tel"
                                    name="userName"
                                    value={formData.userName}
                                    onChange={handleInputChange}
                                    className={styles.adminLoginInput}
                                    placeholder="Nhập số điện thoại của bạn"
                                    autoComplete="tel"
                                />
                            </div>
                        </div>

                        {/* Mật khẩu */}
                        <div className={styles.adminLoginField}>
                            <div className={styles.adminLoginLabelRow}>
                                <label className={styles.adminLoginLabel}>MẬT KHẨU</label>
                                <button type="button" className={styles.adminLoginForgot}>
                                    Quên mật khẩu?
                                </button>
                            </div>
                            <div className={styles.adminLoginInputWrapper}>
                                <span className={styles.adminLoginInputIcon}>
                                    <Lock />
                                </span>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="passWord"
                                    value={formData.passWord}
                                    onChange={handleInputChange}
                                    className={styles.adminLoginInput}
                                    placeholder="Nhập mật khẩu"
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className={styles.adminLoginTogglePassword}
                                    onClick={() => setShowPassword(!showPassword)}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff /> : <Eye />}
                                </button>
                            </div>
                        </div>

                        {/* Ghi nhớ đăng nhập */}
                        <label className={styles.adminLoginRemember}>
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                className={styles.adminLoginCheckbox}
                            />
                            <span className={styles.adminLoginRememberLabel}>Ghi nhớ đăng nhập</span>
                        </label>

                        {/* Error message */}
                        {error && <div className={styles.adminLoginError}>{error}</div>}

                        {/* Submit button */}
                        <button type="submit" className={styles.adminLoginSubmitBtn} disabled={isLoading}>
                            {isLoading ? 'Đang đăng nhập...' : 'Đăng nhập'}
                            {!isLoading && <ArrowRight />}
                        </button>
                    </form>

                    {/* Divider */}
                    <div className={styles.adminLoginDivider}>
                        <span className={styles.adminLoginDividerText}>Bạn chưa có tài khoản?</span>
                    </div>

                    {/* Create Account */}
                    <button className={styles.adminLoginCreateBtn} onClick={() => navigate('/admin/register')}>
                        <UserPlus />
                        Tạo tài khoản
                    </button>

                    {/* App badges */}
                    <div className={styles.adminLoginAppBadges}>
                        <button type="button" className={styles.adminLoginAppBadge} onClick={() => navigate('/tai-app?os=android')}>
                            <Monitor />
                            Android
                        </button>
                        <button type="button" className={styles.adminLoginAppBadge} onClick={() => navigate('/tai-app?os=ios')}>
                            <Smartphone />
                            iOS
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
